/**
 * ScoringEngineView — Tools ▸ Scoring Engine (P0 punch-list fix).
 *
 * The Tools dropdown always advertised a "Scoring Engine" entry, but the
 * route dead-ended on the Tools hub — the operator clicked it and got
 * nothing. This view is the real destination: the control panel for the
 * scoring machinery Argo OS already runs everywhere else.
 *
 * It surfaces, from LIVE records (no fabricated numbers, nothing stored):
 *   - Scoring config — the actual band cut-offs both engines use
 *     (lead cold/warm/hot, health at-risk/watch/healthy), pinned to the
 *     scorers' real rules by `crm/scoringOverview` tests.
 *   - Lead-score analytics — distribution, average, hottest leads with
 *     their transparent factor breakdowns (crm/score.ts).
 *   - Customer-health analytics — distribution, average, riskiest live
 *     accounts, exactly matching the Customer Success cockpit's ranking
 *     (crm/health.ts).
 *   - The live AQI — the same five-component weighted blend the Dashboard
 *     dial runs (crm/aqi.ts), with each component's weight, score, and
 *     human-readable receipt.
 *   - Book-wide score drivers — which signals are adding or dragging
 *     points across every scored record.
 *
 * Everything recomputes from the subscribed snapshots, so this surface can
 * never drift from what the engines actually produce.
 */

import { useEffect, useMemo, useState } from 'react';
import { Activity, Gauge, Flame, HeartPulse, TrendingUp, TrendingDown, SlidersHorizontal } from 'lucide-react';
import { GlassPanel } from '../components/GlassPanel';
import { C } from '../lib/tokens';
import { bootstrapPeopleIfEmpty, subscribePeople } from '../lib/db/people';
import { bootstrapTasksIfEmpty, subscribeTasks } from '../lib/db/tasks';
import { bootstrapAccountsIfEmpty, subscribeAccounts } from '../lib/db/accounts';
import { bootstrapTicketsIfEmpty, subscribeTickets } from '../lib/db/tickets';
import { subscribeAgents } from '../lib/db/agents';
import type { Account, DeployedAgent, Person, ProjectTask, Ticket } from '../lib/db/types';
import type { LeadBand } from '../lib/crm/score';
import type { HealthBand } from '../lib/crm/health';
import { aqiBand, computeAqi } from '../lib/crm/aqi';
import {
  LEAD_BAND_RULES,
  HEALTH_BAND_RULES,
  AQI_BAND_RULES,
  summarizeLeadScores,
  summarizeCustomerHealth,
  aggregateScoreFactors,
  type BandRule,
  type FactorStat,
} from '../lib/crm/scoringOverview';

/** Per-band presentation for the lead scorer. */
const LEAD_BAND_META: Record<LeadBand, { label: string; color: string; pill: string }> = {
  hot: { label: 'Hot', color: C.magenta, pill: 'glass-pill glass-pill--magenta' },
  warm: { label: 'Warm', color: C.orange, pill: 'glass-pill glass-pill--orange' },
  cold: { label: 'Cold', color: '#7ec1ff', pill: 'glass-pill glass-pill--blue' },
};

/** Per-band presentation for the health scorer. */
const HEALTH_BAND_META: Record<HealthBand, { label: string; color: string; pill: string }> = {
  'at-risk': { label: 'At risk', color: C.magenta, pill: 'glass-pill glass-pill--magenta' },
  watch: { label: 'Watch', color: C.orange, pill: 'glass-pill glass-pill--orange' },
  healthy: { label: 'Healthy', color: C.green, pill: 'glass-pill glass-pill--green' },
};

/** Per-band presentation for the AQI (crm/aqi.ts `aqiBand`). */
const AQI_BAND_META: Record<ReturnType<typeof aqiBand>, { label: string; color: string; pill: string }> = {
  strong: { label: 'Strong', color: C.green, pill: 'glass-pill glass-pill--green' },
  steady: { label: 'Steady', color: C.orange, pill: 'glass-pill glass-pill--orange' },
  slipping: { label: 'Slipping', color: C.magenta, pill: 'glass-pill glass-pill--magenta' },
};

/** '+15 · Touched this week' / '−25 · Silent 120d' — math stays visible. */
function factorText(points: number, label: string): string {
  const sign = points < 0 ? '−' : '+';
  return `${sign}${Math.abs(points)} · ${label}`;
}

export function ScoringEngineView() {
  const [people, setPeople] = useState<Person[]>([]);
  const [tasks, setTasks] = useState<ProjectTask[]>([]);
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [tickets, setTickets] = useState<Ticket[]>([]);
  const [agents, setAgents] = useState<DeployedAgent[]>([]);

  useEffect(() => {
    let active = true;
    void bootstrapPeopleIfEmpty();
    void bootstrapTasksIfEmpty();
    void bootstrapAccountsIfEmpty();
    void bootstrapTicketsIfEmpty();
    const unsubs = [
      subscribePeople((r) => active && setPeople(r)),
      subscribeTasks((r) => active && setTasks(r)),
      subscribeAccounts((r) => active && setAccounts(r)),
      subscribeTickets((r) => active && setTickets(r)),
      subscribeAgents((r) => active && setAgents(r)),
    ];
    return () => {
      active = false;
      unsubs.forEach((u) => u());
    };
  }, []);

  // On-demand roll-ups over the live records — recomputed, never stored.
  const leads = useMemo(() => summarizeLeadScores(people), [people]);
  const health = useMemo(() => summarizeCustomerHealth(people, tasks), [people, tasks]);
  const drivers = useMemo(
    () =>
      aggregateScoreFactors([
        ...leads.ranked.map((r) => r.score.factors),
        ...health.ranked.map((r) => r.health.factors),
      ]),
    [leads, health],
  );

  // The company-wide AQI — same pure engine the Dashboard dial runs.
  const aqi = useMemo(
    () => computeAqi({ people, accounts, tickets, tasks, agents }),
    [people, accounts, tickets, tasks, agents],
  );
  const aqiMeta = AQI_BAND_META[aqiBand(aqi.score)];

  const topLeads = leads.ranked.slice(0, 6);
  const riskiest = health.ranked.slice(0, 6);

  return (
    <div className="app-container" style={{ display: 'grid', gap: 24 }}>
      <header>
        <span className="t-eyebrow">Tools</span>
        <h1 className="t-page-title" style={{ margin: '6px 0 0', display: 'flex', alignItems: 'center', gap: 10 }}>
          <Gauge size={26} strokeWidth={1.75} style={{ color: C.magentaSoft }} aria-hidden="true" />
          Scoring Engine
        </h1>
        <p style={{ margin: '4px 0 0', color: C.textMuted }} className="t-body">
          How Argo OS scores everything, with the live numbers — lead heat, customer health,
          and the company-wide AQI, computed on demand from real records by the same pure
          engines the rest of the app runs. Nothing here is stored, so it can never drift.
        </p>
      </header>

      {/* Scoring config — the engines' actual band cut-offs. */}
      <section aria-label="Scoring configuration">
        <div style={{ marginBottom: 12, display: 'flex', alignItems: 'center', gap: 8 }}>
          <SlidersHorizontal size={16} strokeWidth={1.75} style={{ color: C.textMuted }} aria-hidden="true" />
          <div>
            <span className="t-eyebrow">Configuration</span>
            <h2 className="t-section-title" style={{ margin: '2px 0 0' }}>Band thresholds</h2>
          </div>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: 12 }}>
          <ConfigCard
            title="Lead score (crm/score)"
            subtitle="Prospects + customers, 0–100. Stage/status, deal value, touch recency, overdue follow-up. Closed-lost and churned pin cold."
            rules={LEAD_BAND_RULES}
            metaOf={(band) => LEAD_BAND_META[band as LeadBand]}
          />
          <ConfigCard
            title="Customer health (crm/health)"
            subtitle="Live customers, 0–100 from a healthy baseline debited by real risk: past-due billing, renewal proximity, silence, blocked/overdue work."
            rules={HEALTH_BAND_RULES}
            metaOf={(band) => HEALTH_BAND_META[band as HealthBand]}
          />
          <ConfigCard
            title="Argo Quality Index (crm/aqi)"
            subtitle="The whole operation, 0–100: five weighted components — follow-up discipline, pipeline freshness, ticket flow, task hygiene, agent leverage — each with a visible receipt."
            rules={AQI_BAND_RULES}
            metaOf={(band) => AQI_BAND_META[band as ReturnType<typeof aqiBand>]}
          />
        </div>
      </section>

      {/* AQI — the company-wide scoreboard, component by component. */}
      <section aria-label="Argo Quality Index">
        <div style={{ marginBottom: 12, display: 'flex', alignItems: 'center', gap: 8 }}>
          <Activity size={16} strokeWidth={1.75} style={{ color: aqiMeta.color }} aria-hidden="true" />
          <div>
            <span className="t-eyebrow">Analytics</span>
            <h2 className="t-section-title" style={{ margin: '2px 0 0' }}>
              Argo Quality Index · {aqi.score} · {aqiMeta.label.toLowerCase()}
            </h2>
          </div>
        </div>
        <GlassPanel variant="default" style={{ padding: 20, display: 'grid', gap: 14 }}>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 12, flexWrap: 'wrap' }}>
            <span style={{ fontSize: 40, fontWeight: 600, lineHeight: 1, color: aqiMeta.color }}>
              {aqi.score}
            </span>
            <span className={aqiMeta.pill}>{aqiMeta.label}</span>
            <span className="t-meta" style={{ color: C.textMuted }}>
              Weighted blend of the five components below — every point traceable to a live record.
            </span>
          </div>
          <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'grid', gap: 10 }}>
            {aqi.components.map((c) => (
              <li key={c.key} style={{ display: 'grid', gap: 4 }}>
                <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, flexWrap: 'wrap' }}>
                  <span className="t-body" style={{ minWidth: 180 }}>{c.label}</span>
                  <span className="t-meta" style={{ color: C.textDim }}>weight {c.weight}%</span>
                  <span className="t-meta" style={{ marginLeft: 'auto', color: AQI_BAND_META[aqiBand(c.score)].color, whiteSpace: 'nowrap' }}>
                    {c.score}
                  </span>
                </div>
                <div
                  aria-hidden="true"
                  style={{ height: 6, borderRadius: 3, background: 'rgba(255,255,255,0.08)', overflow: 'hidden' }}
                >
                  <div
                    style={{
                      width: `${c.score}%`,
                      height: '100%',
                      borderRadius: 3,
                      background: AQI_BAND_META[aqiBand(c.score)].color,
                      opacity: 0.85,
                    }}
                  />
                </div>
                <span className="t-meta" style={{ color: C.textMuted }}>{c.detail}</span>
              </li>
            ))}
          </ul>
        </GlassPanel>
      </section>

      {/* Lead-score analytics. */}
      <section aria-label="Lead score analytics">
        <div style={{ marginBottom: 12, display: 'flex', alignItems: 'center', gap: 8 }}>
          <Flame size={16} strokeWidth={1.75} style={{ color: C.magentaSoft }} aria-hidden="true" />
          <div>
            <span className="t-eyebrow">Analytics</span>
            <h2 className="t-section-title" style={{ margin: '2px 0 0' }}>
              Lead scores · {leads.total} scored · avg {leads.average}
            </h2>
          </div>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: 12 }}>
          <GlassPanel variant="default" style={{ padding: 20, display: 'grid', gap: 12 }}>
            <span className="t-meta" style={{ color: C.textMuted }}>Distribution</span>
            <DistributionBars
              rows={(['hot', 'warm', 'cold'] as LeadBand[]).map((b) => ({
                label: LEAD_BAND_META[b].label,
                color: LEAD_BAND_META[b].color,
                count: leads.bands[b],
              }))}
              total={leads.total}
            />
          </GlassPanel>
          <GlassPanel variant="default" style={{ padding: 20, display: 'grid', gap: 10 }}>
            <span className="t-meta" style={{ color: C.textMuted }}>Hottest leads</span>
            {topLeads.length === 0 ? (
              <p className="t-body" style={{ margin: 0, color: C.textMuted }}>
                No scorable records yet — add prospects or customers in Contacts.
              </p>
            ) : (
              <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'grid', gap: 8 }}>
                {topLeads.map(({ person, score }) => (
                  <li key={person.id} style={{ display: 'flex', alignItems: 'baseline', gap: 10 }}>
                    <span className={LEAD_BAND_META[score.band].pill}>{score.score}</span>
                    <span className="t-body" style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {person.name}
                      {person.company ? <span style={{ color: C.textMuted }}> · {person.company}</span> : null}
                    </span>
                    <span className="t-meta" style={{ color: C.textMuted, whiteSpace: 'nowrap' }}>
                      {score.factors.slice(0, 2).map((f) => factorText(f.points, f.label)).join('  ')}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </GlassPanel>
        </div>
      </section>

      {/* Customer-health analytics. */}
      <section aria-label="Customer health analytics">
        <div style={{ marginBottom: 12, display: 'flex', alignItems: 'center', gap: 8 }}>
          <HeartPulse size={16} strokeWidth={1.75} style={{ color: C.green }} aria-hidden="true" />
          <div>
            <span className="t-eyebrow">Analytics</span>
            <h2 className="t-section-title" style={{ margin: '2px 0 0' }}>
              Customer health · {health.total} live · avg {health.average}
            </h2>
          </div>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: 12 }}>
          <GlassPanel variant="default" style={{ padding: 20, display: 'grid', gap: 12 }}>
            <span className="t-meta" style={{ color: C.textMuted }}>Distribution</span>
            <DistributionBars
              rows={(['at-risk', 'watch', 'healthy'] as HealthBand[]).map((b) => ({
                label: HEALTH_BAND_META[b].label,
                color: HEALTH_BAND_META[b].color,
                count: health.bands[b],
              }))}
              total={health.total}
            />
          </GlassPanel>
          <GlassPanel variant="default" style={{ padding: 20, display: 'grid', gap: 10 }}>
            <span className="t-meta" style={{ color: C.textMuted }}>Riskiest live accounts</span>
            {riskiest.length === 0 ? (
              <p className="t-body" style={{ margin: 0, color: C.textMuted }}>
                No live customers yet — the health engine scores active accounts.
              </p>
            ) : (
              <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'grid', gap: 8 }}>
                {riskiest.map(({ person, health: h }) => (
                  <li key={person.id} style={{ display: 'flex', alignItems: 'baseline', gap: 10 }}>
                    <span className={HEALTH_BAND_META[h.band].pill}>{h.score}</span>
                    <span className="t-body" style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {person.name}
                      {person.company ? <span style={{ color: C.textMuted }}> · {person.company}</span> : null}
                    </span>
                    <span className="t-meta" style={{ color: C.textMuted, whiteSpace: 'nowrap' }}>
                      {h.factors.slice(0, 2).map((f) => factorText(f.points, f.label)).join('  ')}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </GlassPanel>
        </div>
      </section>

      {/* Book-wide score drivers. */}
      <section aria-label="Score drivers">
        <div style={{ marginBottom: 12 }}>
          <span className="t-eyebrow">Analytics</span>
          <h2 className="t-section-title" style={{ margin: '4px 0 0' }}>What is moving scores</h2>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: 12 }}>
          <DriversCard
            title="Top positive drivers"
            icon={TrendingUp}
            color={C.green}
            stats={drivers.positive.slice(0, 6)}
            empty="No positive signals yet."
          />
          <DriversCard
            title="Top score drags"
            icon={TrendingDown}
            color={C.magenta}
            stats={drivers.negative.slice(0, 6)}
            empty="No risk signals firing — clean book."
          />
        </div>
      </section>
    </div>
  );
}

/** One scorer's displayed config: band rows with threshold ranges. */
function ConfigCard({
  title,
  subtitle,
  rules,
  metaOf,
}: {
  title: string;
  subtitle: string;
  rules: readonly BandRule[];
  metaOf: (band: string) => { label: string; color: string; pill: string };
}) {
  return (
    <GlassPanel variant="default" style={{ padding: 20, display: 'grid', gap: 10 }}>
      <h3 className="t-section-title" style={{ margin: 0 }}>{title}</h3>
      <p className="t-meta" style={{ margin: 0, color: C.textMuted }}>{subtitle}</p>
      <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'grid', gap: 8 }}>
        {rules.map((r) => {
          const meta = metaOf(r.band);
          return (
            <li key={r.band} style={{ display: 'flex', alignItems: 'baseline', gap: 10 }}>
              <span className={meta.pill} style={{ whiteSpace: 'nowrap' }}>
                {meta.label} · {r.min}–{r.max}
              </span>
              <span className="t-meta" style={{ color: C.textMuted }}>{r.description}</span>
            </li>
          );
        })}
      </ul>
    </GlassPanel>
  );
}

/** Horizontal count bars for a band distribution. */
function DistributionBars({
  rows,
  total,
}: {
  rows: { label: string; color: string; count: number }[];
  total: number;
}) {
  if (total === 0) {
    return (
      <p className="t-body" style={{ margin: 0, color: C.textMuted }}>
        Nothing scored yet.
      </p>
    );
  }
  return (
    <div style={{ display: 'grid', gap: 8 }}>
      {rows.map((r) => {
        const pct = Math.round((r.count / total) * 100);
        return (
          <div key={r.label} style={{ display: 'grid', gap: 4 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <span className="t-meta">{r.label}</span>
              <span className="t-meta" style={{ color: C.textMuted }}>
                {r.count} · {pct}%
              </span>
            </div>
            <div
              aria-hidden="true"
              style={{
                height: 6,
                borderRadius: 3,
                background: 'rgba(255,255,255,0.08)',
                overflow: 'hidden',
              }}
            >
              <div
                style={{
                  width: `${pct}%`,
                  height: '100%',
                  borderRadius: 3,
                  background: r.color,
                  opacity: 0.85,
                }}
              />
            </div>
          </div>
        );
      })}
    </div>
  );
}

/** Aggregated factor table (positive drivers or drags). */
function DriversCard({
  title,
  icon: Icon,
  color,
  stats,
  empty,
}: {
  title: string;
  icon: typeof TrendingUp;
  color: string;
  stats: FactorStat[];
  empty: string;
}) {
  return (
    <GlassPanel variant="default" style={{ padding: 20, display: 'grid', gap: 10 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <Icon size={16} strokeWidth={1.75} style={{ color }} aria-hidden="true" />
        <span className="t-meta" style={{ color: C.textMuted }}>{title}</span>
      </div>
      {stats.length === 0 ? (
        <p className="t-body" style={{ margin: 0, color: C.textMuted }}>{empty}</p>
      ) : (
        <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'grid', gap: 6 }}>
          {stats.map((f) => (
            <li key={f.label} style={{ display: 'flex', alignItems: 'baseline', gap: 10 }}>
              <span className="t-body" style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {f.label}
              </span>
              <span className="t-meta" style={{ color: C.textMuted, whiteSpace: 'nowrap' }}>
                ×{f.count}
              </span>
              <span className="t-meta" style={{ color, whiteSpace: 'nowrap' }}>
                {`${f.totalPoints < 0 ? '−' : '+'}${Math.abs(f.totalPoints)}`}
              </span>
            </li>
          ))}
        </ul>
      )}
    </GlassPanel>
  );
}
